"use client";

import { FC, useCallback, useEffect, useState } from "react";
import { useTranslations, useLocale } from "next-intl";
import { ApolloProvider, useLazyQuery } from "@apollo/client";

import { client } from "services";
import { DEFAULT_SEARCH_RADIUS_DATO, SLUGS } from "consts";
import { getCmsLocale, useGetGeoLocation } from "utils";
import { GeoStatus, IconType, OfficeRecord } from "@types";
import { SEARCH_OFFICES_BY_GEOLOCATION_QUERY } from "@graphql";

import { Button } from "components/atoms/Button/Button";
import { CancelButton } from "components/atoms/CancelButton/CancelButton";
import { OfficeTeaser } from "components/molecules/Teaser/OfficeTeaser";
import { Section } from "components/molecules/Section/Section";
import { Container } from "components/organisms/Container/Container";
import { Grid } from "components/organisms/Grid/Grid";

import s from "./NearbySection.module.scss";
import { Locale } from "consts/locale";

interface Props {
  offices?: OfficeRecord[];
}

const NearbyComponent: FC<Props> = ({ offices }) => {
  const locale = useLocale() as Locale;
  const t = useTranslations("common");
  const tSearch = useTranslations("search");

  const [geoActive, setGeoActive] = useState(false);
  const [nearbyOffices, setNearbyOffices] = useState<OfficeRecord[] | undefined>(offices);

  const { currentCoordinates, currentMunicipality, getGeoCodedLocation, status } =
    useGetGeoLocation();
  const [executeSearch, { data }] = useLazyQuery(SEARCH_OFFICES_BY_GEOLOCATION_QUERY);

  const getStatusLabel = useCallback(() => {
    let statusLabel = tSearch("use-location");

    if (!geoActive && status !== GeoStatus.Pending) {
      return tSearch("use-location");
    }

    switch (status) {
      case GeoStatus.Pending:
        statusLabel = `${tSearch("pending-location")}...`;
        break;
      case GeoStatus.Success:
        statusLabel = `Locatie: ${currentMunicipality}`;
        break;
      case GeoStatus.Timeout:
      case GeoStatus.Unavailable:
      case GeoStatus.Denied:
        statusLabel = tSearch("error-location");
        break;
      default:
    }

    return statusLabel;
  }, [currentMunicipality, status, tSearch, geoActive]);

  useEffect(() => {
    if (currentCoordinates?.latitude && currentCoordinates?.longitude) {
      executeSearch({
        variables: {
          filter: {
            officeSearchLocation: {
              near: {
                latitude: currentCoordinates.latitude,
                longitude: currentCoordinates.longitude,
                radius: DEFAULT_SEARCH_RADIUS_DATO,
              },
            },
          },
          locale: getCmsLocale(locale),
        },
      });
    }
  }, [currentCoordinates, executeSearch, locale]);

  useEffect(() => {
    if (data?.results) {
      setNearbyOffices(data.results);
      setGeoActive(true);
    }
  }, [data]);

  return (
    <Section background>
      <Container>
        <div className={s.Inner}>
          <div className={s.Top}>
            <h2 className={s.Title}>{t("our-offices")}</h2>
            <div className={s.TopActions}>
              <Button
                label={getStatusLabel()}
                theme="textual"
                onClick={() => {
                  if (!geoActive) {
                    getGeoCodedLocation();
                  }
                }}
                icon={IconType.Location}
                iconSize="l"
                preIcon
                disabled={status === GeoStatus.Pending}
              />
              {geoActive && (
                <CancelButton
                  onClick={() => {
                    setNearbyOffices(offices);
                    setGeoActive(false);
                  }}
                />
              )}
            </div>
          </div>
          {nearbyOffices && (
            <Grid columns="3">
              {nearbyOffices.map((office) => (
                <OfficeTeaser office={office} key={office.id} />
              ))}
            </Grid>
          )}
          <Button
            label={t("discover-all-offices")}
            link={{ href: SLUGS.nearby }}
            theme="textual"
            icon={IconType.CaretFilled}
          />
        </div>
      </Container>
    </Section>
  );
};

export const NearbySection = (props: Props) => (
  <ApolloProvider client={client}>
    <NearbyComponent {...props} />
  </ApolloProvider>
);
